import { debuggerSetup } from "./editorSetup.js";
import { EditorState } from "@codemirror/state";
import { Facet, RangeSetBuilder } from "@codemirror/state";
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
} from "@codemirror/view";
import * as highlightPatterns from "./highlightPatterns.js";

export type LineRange = {
  startLn: number;
  startCol: number;
  endLn: number;
  endCol: number;
  endMarkers: string;
};

export const facet = Facet.define<LineRange[], LineRange[]>({
  combine: (rangeArrays) => rangeArrays.flat(),
});

const endMarkerSegmentMap = new Map([
  ["cc", Decoration.mark({ attributes: { class: "flow-segment" } })],
  [
    "co",
    Decoration.mark({
      attributes: { class: "flow-segment", "data-end-open": "" },
    }),
  ],
  [
    "oc",
    Decoration.mark({
      attributes: { class: "flow-segment", "data-start-open": "" },
    }),
  ],
  [
    "oo",
    Decoration.mark({
      attributes: {
        class: "flow-segment",
        "data-start-open": "",
        "data-end-open": "",
      },
    }),
  ],
]);

export const viewPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      const lineRanges = view.state.facet(facet);
      const builder = new RangeSetBuilder<Decoration>();
      for (const {
        startLn,
        startCol,
        endLn,
        endCol,
        endMarkers,
      } of lineRanges) {
        const startLine = view.state.doc.line(startLn);
        const endLine = view.state.doc.line(endLn);
        builder.add(
          startLine.from + startCol - 1,
          endLine.from + endCol - 1,
          endMarkerSegmentMap.get(endMarkers)!
        );
      }
      this.decorations = builder.finish();
    }

    // No need to update because our editors are read-only.
    update(update: ViewUpdate) {}
  },
  {
    decorations: (v) => v.decorations,
  }
);

export class ConditionalFlow extends HTMLElement {
  #template: HTMLTemplateElement;
  #shadowRoot: ShadowRoot;
  #sourceCode: string = "";
  #container: HTMLDivElement;
  editor: EditorView | null = null;
  #nodes: LineRange[] = [];
  #edges: { from: number; to: number }[] = [];

  constructor() {
    super();
    this.#template = document.querySelector("#conditional-flow")!;
    this.#shadowRoot = this.attachShadow({ mode: "open" });
    this.#shadowRoot.appendChild(this.#template.content.cloneNode(true));
    this.#container = this.#shadowRoot.querySelector("div.code-block")!;
  }

  connectedCallback() {
    console.group("ConditionalFlow.connectedCallback");
    const example = this.getAttribute("example");
    const flowsValue = this.getAttribute("flows");
    if (example !== null) {
      const template = document.querySelector("pre#code-example-" + example);
      if (template instanceof HTMLPreElement) {
        if (template.textContent === null) {
          console.error(
            `Code example "${example}" contains a <pre> element with no text content`
          );
        } else {
          this.#sourceCode = template.textContent;
        }
      } else {
        console.error(`Code example "${example}" not found`);
      }
    }
    if (typeof flowsValue === "string") {
      const flows = JSON.parse(flowsValue);
      for (const [
        [startLn, startCol],
        [endLn, endCol],
        endMarkers,
      ] of flows.nodes) {
        this.#nodes.push({ startLn, startCol, endLn, endCol, endMarkers });
      }
      console.log(this.#nodes);
    } else {
      console.error("Invalid flows attribute");
    }
    const breakpointLine = this.dataset.breakpointLine
      ? Number(this.dataset.breakpointLine)
      : NaN;
    if (this.editor) {
      this.editor.dispatch({
        changes: {
          from: 0,
          to: this.#sourceCode.length,
          insert: this.#sourceCode,
        },
      });
    } else {
      this.editor = new EditorView({
        parent: this.#container,
        state: EditorState.create({
          doc: this.#sourceCode,
          extensions: [
            debuggerSetup,
            EditorView.editable.of(false),
            facet.of(this.#nodes),
            viewPlugin,
            highlightPatterns.breakpointLineFacet.of(breakpointLine),
            highlightPatterns.viewPlugin,
          ],
        }),
      });
    }
    console.groupEnd();
  }

  disconnectedCallback() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  static observedAttributes = ["example", "flows"];
}

customElements.define("conditional-flow", ConditionalFlow);
